/**
 * Карточка сети на `/posts/:id` — «Предпросмотр публикации» (макеты Оли №383): ОДНА картинка — шапка
 * «аккаунт · сеть» и рамка кадра прямо в ней (прежде превью с текстом и рамка шли двумя копиями кадра);
 * под ней карусель; справа поля (заголовок, текст, хэштеги, alt) со счётчиками лимитов, предупреждения
 * генерации, «Прошлые генерации»; в шапке единственная кнопка генерации; внизу СВОЁ утверждение сети.
 *
 * Утверждают сеть, а не публикацию (№285), поэтому кнопка «Утвердить ‹Сеть›» живёт здесь, а не в
 * общем подвале страницы: рядом с текстом, который утверждают, и с причинами именно этой сети. Там же
 * дата выкладки — «Выложить: …» и окно-календарь сети `ScheduleDialog` (№385).
 * Статус, утвердившего и дату сеть несёт сама (`PostNetwork.status`), как и готовую строку «возьмёт
 * только обложку…» (`media_note`) — предупреждение, а не запрет утверждения. Так же — строка дубля
 * кадра в Instagram (`dup_parts`, №242): «публикация #M» в ней — ссылка на `/posts/M`.
 *
 * Рамка — у ЭЛЕМЕНТА карусели, не у публикации: под рамкой карусель (`CarouselBar`, бывшая полоса «Что
 * публикуем») — миниатюры при двух элементах и больше, у выбранного «‹ › убрать», «+ в карусель»; выбранный
 * показан и кадрируется (по умолчанию — обложка); видео-элемент — постер без рамки, рамку для него не
 * предлагаем (спека §4). Выбор элемента — состояние самой карточки, состав карусели — общий у публикации
 * (утверждённая сеть его морозит, `carousel.note` говорит почему).
 *
 * Чистый компонент по пропсам: текстовые поля уходят `onEdit` (ввод — с задержкой), рамка — `onCropChange`
 * сразу по отпусканию, сохраняет страница. Поле в фокусе держит свой текст: ответ сервера (нормализованные
 * хэштеги) не правит строку под курсором.
 */
import clsx from "clsx";
import { ChevronLeft, ChevronRight, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";

import { CropFrame } from "@/components/posts/CropFrame";
import { ScheduleDialog } from "@/components/posts/ScheduleDialog";
import { btn, btnGhost, btnPrimary, input, label } from "@/components/trends/ui";
import { mediaUrl, thumbUrl, when } from "@/lib/media";
import type { Aspect } from "@/lib/post";
import type { NetEdit } from "@/lib/postDraft";
import { whenLabel } from "@/lib/postsTabs";
import { cover, elementsWord, mediaCrop, videoCaption, type PostMedia } from "@/lib/postMedia";
import {
  dupShown,
  fullText,
  genLabel,
  hashtagCount,
  NETWORK_LABEL,
  normalizeHashtags,
  type Crop,
  type PostFormat,
  type PostGen,
  type PostNetwork,
  type PostNetworkCode,
} from "@/lib/postPlan";

/** Состав карусели — общий у публикации: правит страница той же дорожкой, что переходы. */
export interface CarouselProps {
  /** не черновик или утверждена хоть одна сеть */
  readOnly: boolean;
  /** идёт другая правка — кнопки гаснут */
  busy: boolean;
  /** почему состав не правится (утверждённая сеть); `null` — обычный черновик */
  note: string | null;
  onMove: (mediaId: number, dir: -1 | 1) => void;
  onRemove: (mediaId: number) => void;
  onAdd: () => void;
}

/** Дата выкладки сети (№385): правится и у утверждённой сети, сохраняет страница. */
export interface ScheduleProps {
  planId: number;
  value: string | null;
  readOnly: boolean;
  onSave: (date: string | null) => void;
}

export interface NetworkCardProps {
  code: PostNetworkCode;
  net: PostNetwork;
  format: PostFormat;
  gens: PostGen[];
  /** Элементы карусели по порядку (обложка — `postMedia.cover`, первый по умолчанию кадрируется).
   * Переключатель под рамкой меняет, какой элемент показан и кадрируется; видео — постер без рамки. */
  media: PostMedia[];
  carousel: CarouselProps;
  schedule: ScheduleProps;
  /** ник первого отмеченного аккаунта — в шапку превью */
  handle: string;
  readOnly: boolean;
  /** идёт генерация этой сети */
  generating: boolean;
  /** последняя генерация не тронула правленое — новый вариант в «Прошлых генерациях» */
  kept: boolean;
  error: string | null;
  /** «Готово · $…» после генерации этой сети */
  done: string | null;
  /** оценка одного вызова генерации (`plan.estimate`) и шаблоны, по которым он пойдёт, — в кнопку и подсказку */
  estimate: number;
  templates: string;
  /** Почему эту сеть нельзя утвердить: общее публикации плюс её причины (`postPlan.netApproveProblems`);
   * пусто — можно утверждать. */
  problems: string[];
  /** Переход статуса уже идёт или публикация не правится — кнопки утверждения гаснут (но не поля:
   * у них свой `readOnly`, он считается на сеть). */
  stateBusy: boolean;
  /** Отказ перехода ИМЕННО этой сети (`NOT_APPROVABLE` и прочее). */
  stateError: string | null;
  onEdit: (fields: NetEdit, immediate: boolean) => void;
  onRegenerate: () => void;
  onTake: (genId: number) => void;
  /** Рамка выбранного элемента для ЭТОЙ сети; `null` снимает рамку сети (`postsApi.setMediaCrop`). */
  onCropChange: (mediaId: number, crop: Crop | null) => void;
  /** Утвердить / вернуть в черновик / отменить ЭТУ сеть (`networks: [код]`). */
  onApprove: () => void;
  onUnapprove: () => void;
  onCancelNetwork: () => void;
}

/** Длина в символах, как `len` в Python. */
const chars = (s: string) => [...s].length;

function Counter({ n, max, min }: { n: number; max: number; min?: number | null }) {
  const bad = n > max || (min ? n > 0 && n < min : false);
  return (
    <span className={clsx("tnum text-[11px]", bad ? "font-medium text-err" : "text-fg-faint")}>
      {n} / {max}
    </span>
  );
}

function Field({
  caption,
  value,
  onChange,
  onBlurValue,
  rows,
  disabled,
  counter,
  hint,
  placeholder,
}: {
  caption: string;
  value: string;
  onChange: (v: string) => void;
  /** по потере фокуса: итоговое значение (хэштеги — нормализованные) */
  onBlurValue?: (v: string) => string;
  rows?: number;
  disabled: boolean;
  counter: ReactNode;
  hint?: string | undefined;
  placeholder?: string | undefined;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  // поле погасло в фокусе (генерация, утверждение) — blur браузер может не прислать
  useEffect(() => {
    if (disabled) setDraft(null);
  }, [disabled]);
  const shown = draft ?? value;
  const common = {
    "aria-label": caption,
    value: shown,
    disabled,
    placeholder,
    onFocus: () => setDraft(value),
    onChange: (e: { target: { value: string } }) => {
      setDraft(e.target.value);
      onChange(e.target.value);
    },
    onBlur: () => {
      if (draft !== null && onBlurValue) {
        const fixed = onBlurValue(draft);
        if (fixed !== value) onChange(fixed);
      }
      setDraft(null);
    },
  };
  return (
    <label className="grid gap-1">
      <span className="flex items-center gap-2">
        <span className={label}>{caption}</span>
        {hint ? <span className="text-[11px] text-fg-faint">{hint}</span> : null}
        <span className="ml-auto">{counter}</span>
      </span>
      {rows ? (
        <textarea {...common} rows={rows} className={`${input} h-auto resize-y py-1.5 leading-snug`} />
      ) : (
        <input {...common} className={input} />
      )}
    </label>
  );
}

/** Переключатель элементов карусели под рамкой — какой кадрируем и показываем в превью. */
/** Карусель под картинкой; страница рисует её и сама, когда карточки сети нет (сеть не включена). */
export function CarouselBar({
  media,
  activeId,
  onPick,
  carousel,
}: {
  media: PostMedia[];
  activeId: number | null;
  onPick: (id: number) => void;
  carousel: CarouselProps;
}) {
  const locked = carousel.readOnly || carousel.busy;
  const index = media.findIndex((m) => m.id === activeId);
  const many = media.length > 1;
  return (
    <div className="grid gap-1.5" data-annot="карусель">
      {carousel.note ? (
        <div className="text-[11.5px] text-warn" data-annot="полоса заморожена">
          {carousel.note}
        </div>
      ) : null}
      {many ? (
        <div className="flex flex-wrap gap-1.5" data-annot="переключатель элементов">
          {media.map((m, i) => {
            const isVideo = m.video_ms !== null;
            const thumb = isVideo ? m.poster_path : m.asset_path;
            return (
              <button
                key={m.id}
                type="button"
                aria-pressed={m.id === activeId}
                title={`${i === 0 ? "обложка" : `элемент ${i + 1}`}${isVideo ? ` · видео ${videoCaption(m.video_ms)}` : ""}`}
                onClick={() => onPick(m.id)}
                className={clsx(
                  "relative size-10 shrink-0 overflow-hidden rounded-[4px] border",
                  m.id === activeId ? "border-accent" : "border-border hover:border-border-strong",
                )}
              >
                {thumb ? (
                  <img src={thumbUrl(thumb)} alt="" className="size-full object-cover" />
                ) : (
                  <span className="grid size-full place-items-center bg-bg-subtle text-[9px] text-fg-faint">—</span>
                )}
                <span className="absolute bottom-0 left-0 bg-black/60 px-0.5 text-[9px] leading-tight text-white">{i + 1}</span>
              </button>
            );
          })}
        </div>
      ) : null}
      {carousel.readOnly ? null : (
        <div className="flex flex-wrap items-center gap-1 text-[11.5px]">
          {many ? (
            <>
              <button
                type="button"
                className={btnGhost}
                disabled={locked || index <= 0}
                onClick={() => activeId !== null && carousel.onMove(activeId, -1)}
                title="Переместить выбранный раньше"
                aria-label="Переместить элемент раньше"
              >
                <ChevronLeft size={13} />
              </button>
              <button
                type="button"
                className={btnGhost}
                disabled={locked || index < 0 || index === media.length - 1}
                onClick={() => activeId !== null && carousel.onMove(activeId, 1)}
                title="Переместить выбранный позже"
                aria-label="Переместить элемент позже"
              >
                <ChevronRight size={13} />
              </button>
              <button
                type="button"
                className={btnGhost}
                disabled={locked || index < 0}
                onClick={() => activeId !== null && carousel.onRemove(activeId)}
                title="Убрать выбранный из карусели"
              >
                <Trash2 size={13} /> убрать
              </button>
            </>
          ) : null}
          <button type="button" className={btnGhost} disabled={locked} onClick={carousel.onAdd} title="Добавить фото или видео в карусель">
            <Plus size={13} /> в карусель
          </button>
          {many ? (
            <span className="tnum text-fg-faint">
              {media.length} {elementsWord(media.length)}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Подпись статуса сети в шапке карточки. */
function StatusChip({ net }: { net: PostNetwork }) {
  if (net.status === "approved") {
    return (
      <span className="rounded-[var(--radius-ui)] bg-ok-bg px-1.5 py-0.5 text-[11px] text-ok">утверждено</span>
    );
  }
  if (net.status === "cancelled") {
    return (
      <span className="rounded-[var(--radius-ui)] bg-bg-subtle px-1.5 py-0.5 text-[11px] text-fg-faint">
        отменена
      </span>
    );
  }
  return null;
}

/**
 * Своё утверждение сети (№285): «Утвердить ‹Сеть›» с причинами ТОЛЬКО этой сети, «Вернуть в
 * черновик» у утверждённой и «Не публиковать в ‹Сеть›» (отмена сети, остальные живут дальше).
 * Соседние сети этим кнопкам безразличны — текст, написанный только для ВК, утверждает ВК.
 */
function ScheduleRow({ code, schedule }: { code: PostNetworkCode; schedule: ScheduleProps }) {
  const [open, setOpen] = useState(false);
  const name = NETWORK_LABEL[code];
  return (
    <div className="flex flex-wrap items-center gap-2 text-[12.5px]" data-annot="дата выкладки">
      <span className="text-fg-muted">Выложить:</span>
      <span className={schedule.value ? "text-accent-text" : "text-fg-faint"}>
        {schedule.value ? whenLabel(schedule.value) : "дата не назначена"}
      </span>
      <button
        type="button"
        className={btn}
        disabled={schedule.readOnly}
        title={`Календарь ${name}: что уже запланировано по дням`}
        onClick={() => setOpen(true)}
      >
        {schedule.value ? "Изменить дату" : "Запланировать…"}
      </button>
      {open ? (
        <ScheduleDialog
          open
          planId={schedule.planId}
          network={code}
          networkName={name}
          value={schedule.value}
          onClose={() => setOpen(false)}
          onSave={(date) => {
            schedule.onSave(date);
            setOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function Approval({
  code,
  net,
  schedule,
  problems,
  busy,
  error,
  onApprove,
  onUnapprove,
  onCancelNetwork,
}: {
  code: PostNetworkCode;
  net: PostNetwork;
  schedule: ScheduleProps;
  problems: string[];
  busy: boolean;
  error: string | null;
  onApprove: () => void;
  onUnapprove: () => void;
  onCancelNetwork: () => void;
}) {
  const name = NETWORK_LABEL[code];
  const ready = problems.length === 0;
  return (
    <div className="grid gap-1.5 border-t border-border pt-3" data-annot={`утверждение ${code}`}>
      {net.status !== "cancelled" ? <ScheduleRow code={code} schedule={schedule} /> : null}
      {net.status === "draft" ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={btnPrimary}
              disabled={busy || !ready}
              title={ready ? `Утверждает только ${name}` : "Сеть не готова — причины ниже"}
              onClick={onApprove}
            >
              Утвердить {name}
            </button>
            <span className="text-[12px] text-fg-faint">
              {ready ? "остальные сети останутся черновиками" : "пока утвердить нельзя"}
            </span>
            <button
              type="button"
              className={`${btn} ml-auto`}
              disabled={busy}
              title={`Убрать ${name} из этой публикации — остальные сети останутся`}
              onClick={onCancelNetwork}
            >
              Не публиковать в {name}
            </button>
          </div>
          {/* имя метки прежнее (аннотатор и golden-тест) — список просто переехал в карточку сети */}
          {problems.length > 0 ? (
            <ul className="grid gap-0.5 text-[12px] text-fg-muted" data-annot="почему нельзя утвердить">
              {problems.map((p) => (
                <li key={p}>• {p}</li>
              ))}
            </ul>
          ) : null}
        </>
      ) : net.status === "approved" ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12.5px] text-ok">
            Утверждено · {net.approver_name ?? "—"} {when(net.date_approve)}
          </span>
          <span className="text-[12px] text-fg-faint">поля этой сети только читаются</span>
          <button
            type="button"
            className={`${btn} ml-auto`}
            disabled={busy}
            title={`Вернуть в черновик только ${name}`}
            onClick={onUnapprove}
          >
            Вернуть в черновик
          </button>
        </div>
      ) : (
        <span className="text-[12.5px] text-fg-faint">
          Сеть отменена — в этой публикации {name} не выкладываем.
        </span>
      )}
      {error ? <div className="text-[12px] text-err">{error}</div> : null}
    </div>
  );
}

function GenLine({ g }: { g: PostGen }) {
  return (
    <span className="text-[11px] text-fg-faint">
      {when(g.date_create)} · шаблон v{g.network_number} · общий v{g.base_number}
    </span>
  );
}

export function NetworkCard(p: NetworkCardProps) {
  const { code, net, format, gens, media, readOnly } = p;
  const name = NETWORK_LABEL[code];
  const title = net.title ?? "";
  const text = net.text ?? "";
  const tags = net.hashtags ?? "";
  const alt = net.alt ?? "";
  const gen = gens.find((g) => g.id === net.gen_id) ?? null;
  const locked = readOnly || p.generating;
  const typed = (k: "title" | "text" | "hashtags" | "alt") => (v: string) => p.onEdit({ [k]: v }, false);

  // Переключатель элементов: по умолчанию — обложка; выбор сбрасывается, если элемент убрали из карусели.
  const [selected, setSelected] = useState<number | null>(null);
  const defaultId = cover(media)?.id ?? media[0]?.id ?? null;
  const activeId = selected !== null && media.some((m) => m.id === selected) ? selected : defaultId;
  const active = media.find((m) => m.id === activeId) ?? null;
  const activeIsVideo = active ? active.video_ms !== null : false;
  const activeCrop = mediaCrop(active ?? undefined, code);
  const dup = dupShown(net);

  return (
    <section
      className="grid gap-3 rounded-[var(--radius-card)] border border-border bg-bg p-3"
      data-annot={`карточка ${code}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-[13px] font-medium text-fg">Предпросмотр публикации</h2>
        <StatusChip net={net} />
        <span className="text-[11.5px] text-fg-faint">
          {gen ? `шаблон ${name} v${gen.network_number} · общий v${gen.base_number}` : "текст не генерировали"}
        </span>
        {net.edited && gen ? (
          <span className="rounded-[var(--radius-ui)] bg-warn-bg px-1.5 py-0.5 text-[11px] text-warn">
            правлено после генерации
          </span>
        ) : null}
        {p.done ? <span className="ml-auto text-[12px] text-ok">{p.done}</span> : null}
        {/* единственная кнопка генерации текста (№379: верхняя «Генерировать ‹Сеть›» звала то же самое) */}
        <button
          type="button"
          className={clsx(btnPrimary, !p.done && "ml-auto")}
          disabled={readOnly || p.generating}
          title={`Платно: один вызов только для этой сети · шаблоны: ${p.templates}`}
          onClick={p.onRegenerate}
        >
          <RefreshCw size={13} className={p.generating ? "animate-spin" : undefined} />
          {genLabel(p.generating, gens.length > 0, p.estimate)}
        </button>
      </div>
      {p.error ? <div className="text-[12px] text-err">{p.error}</div> : null}
      {net.media_note && net.status !== "cancelled" ? (
        <div
          className="rounded-[var(--radius-ui)] border border-warn/40 bg-warn-bg px-2 py-1.5 text-[12px] text-warn"
          data-annot={`лимит элементов ${code}`}
        >
          {name} {net.media_note} — утверждению это не мешает.
        </div>
      ) : null}
      {dup ? (
        <div
          className="rounded-[var(--radius-ui)] border border-warn/40 bg-warn-bg px-2 py-1.5 text-[12px] text-warn"
          data-annot={`дубль кадра ${code}`}
        >
          {name}: {dup.map((part, i) =>
            part.post_id !== null ? (
              <Link key={i} to={`/posts/${part.post_id}`} className="text-accent-text hover:underline">
                {part.text}
              </Link>
            ) : (
              <span key={i}>{part.text}</span>
            ),
          )}{" "}
          — утверждению это не мешает.
        </div>
      ) : null}
      {p.kept ? (
        <div className="rounded-[var(--radius-ui)] border border-warn/40 bg-warn-bg px-2 py-1.5 text-[12px] text-warn">
          Текст правлен — новый вариант в «Прошлых генерациях».
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <div className="grid content-start gap-3">
          <div className="w-full max-w-[320px] overflow-hidden rounded-[8px] border border-border" data-annot={`превью ${code}`}>
            <div className="flex items-center justify-between gap-2 px-3 py-2 text-[12.5px]">
              <span className="truncate font-medium text-fg">{p.handle || "аккаунт"}</span>
              <span className="text-[11px] text-fg-faint">{name}</span>
            </div>
            {activeIsVideo ? (
              <div className="grid gap-1 p-2 pt-0" data-annot="элемент карусели — видео">
                {active?.poster_path ? (
                  <img src={mediaUrl(active.poster_path)} alt="" className="block w-full rounded-[4px]" />
                ) : (
                  <div className="grid aspect-[3/4] place-items-center text-[11px] text-fg-faint">нет постера</div>
                )}
                <span className="text-[11.5px] text-fg-faint">ролик публикуется как есть</span>
              </div>
            ) : (
              <div className="p-2 pt-0">
                <CropFrame
                  src={active ? mediaUrl(active.asset_path) : ""}
                  width={active?.width ?? null}
                  height={active?.height ?? null}
                  aspect={format.aspect as Aspect}
                  crop={activeCrop}
                  format={format}
                  readOnly={locked}
                  onCommit={(next) => active && p.onCropChange(active.id, next)}
                />
              </div>
            )}
          </div>
          <CarouselBar media={media} activeId={activeId} onPick={setSelected} carousel={p.carousel} />
        </div>

        <div className="grid content-start gap-3">
          {format.title_max !== null ? (
            <Field
              caption="Заголовок"
              value={title}
              onChange={typed("title")}
              disabled={locked}
              hint={format.title_fold ? `в ленте видно ~${format.title_fold}` : undefined}
              counter={<Counter n={chars(title)} max={format.title_max} />}
            />
          ) : null}
          <Field
            caption="Текст"
            value={text}
            onChange={typed("text")}
            rows={8}
            disabled={locked}
            hint={format.text_fold ? `до «ещё» ${format.text_fold}` : undefined}
            counter={<Counter n={chars(fullText(text, tags))} max={format.text_max} />}
          />
          {/* №387: сеть без хэштегов (Pinterest) поле не показывает, пока в тексте их нет — старые можно стереть */}
          {format.hashtag_max > 0 || tags ? (
            <Field
              caption="Хэштеги"
              value={tags}
              onChange={typed("hashtags")}
              onBlurValue={normalizeHashtags}
              disabled={locked}
              placeholder="#свадебноеплатье #прокатплатьев"
              hint={format.hashtag_max > 0 ? "лимит текста — вместе с хэштегами" : "эта сеть хэштеги не берёт — сотрите их"}
              counter={<Counter n={hashtagCount(tags)} max={format.hashtag_max} />}
            />
          ) : null}
          {format.alt_max !== null ? (
            <Field
              caption="Alt (описание фото)"
              value={alt}
              onChange={typed("alt")}
              rows={2}
              disabled={locked}
              hint={format.alt_min ? `не короче ${format.alt_min}` : undefined}
              counter={<Counter n={chars(alt)} max={format.alt_max} min={format.alt_min} />}
            />
          ) : null}
          {gen && gen.warnings.length > 0 ? (
            <ul className="grid gap-0.5 text-[12px] text-warn" data-annot={`предупреждения ${code}`}>
              {gen.warnings.map((w) => (
                <li key={w}>⚠ {w}</li>
              ))}
            </ul>
          ) : null}

          {gens.length > 0 ? (
            <details className="rounded-[var(--radius-ui)] border border-border" data-annot={`прошлые генерации ${code}`}>
              <summary className="cursor-pointer px-2 py-1.5 text-[12px] text-fg-muted">
                Прошлые генерации ({gens.length})
              </summary>
              <ul className="grid gap-2 border-t border-border p-2">
                {gens.map((g) => {
                  const current = g.id === net.gen_id;
                  const head = [g.title, g.text].filter(Boolean).join(" · ");
                  return (
                    <li key={g.id} className="grid gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <GenLine g={g} />
                        {current ? <span className="text-[11px] text-accent-text">в карточке</span> : null}
                        <button
                          type="button"
                          className={`${btnGhost} ml-auto`}
                          disabled={locked || (current && !net.edited)}
                          onClick={() => p.onTake(g.id)}
                        >
                          Взять этот вариант
                        </button>
                      </div>
                      <p className="line-clamp-2 text-[12px] text-fg-muted">{head || "—"}</p>
                      {g.hashtags ? <p className="truncate text-[11.5px] text-fg-faint">{g.hashtags}</p> : null}
                    </li>
                  );
                })}
              </ul>
            </details>
          ) : null}
        </div>
      </div>

      <Approval
        code={code}
        net={net}
        schedule={p.schedule}
        problems={p.problems}
        busy={p.stateBusy}
        error={p.stateError}
        onApprove={p.onApprove}
        onUnapprove={p.onUnapprove}
        onCancelNetwork={p.onCancelNetwork}
      />
    </section>
  );
}
